import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { NGXLogger } from "ngx-logger";
import {
  ConfirmationDetails,
  ConfirmationService,
} from "../../confirmation-dialog/confirmation.service";
import { PadlockService } from "../../padlock/padlock.service";
import { ToastService } from "../../shared/toast.service";
import {
  DirectoryUser,
  UsersManagementService,
} from "./users-management.service";

@Component({
  selector: "app-users-management",
  templateUrl: "./users-management.component.html",
  styleUrls: ["./users-management.component.scss"],
  standalone: false,
})
export class UsersManagementComponent implements OnInit {
  displayedColumns: string[] = ["email", "username", "actions"];

  private tablePaginator?: MatPaginator;
  private tableSort?: MatSort;

  @ViewChild(MatPaginator)
  set matPaginator(paginator: MatPaginator | undefined) {
    this.tablePaginator = paginator;
    this.syncTableControls();
  }

  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    this.tableSort = sort;
    this.syncTableControls();
  }

  loading = false;
  loadError: any = null;
  isPlatformAdmin = false;

  users: DirectoryUser[] = [];
  dataSource = new MatTableDataSource<DirectoryUser>([]);
  pageSize = 10;
  searchControl = new UntypedFormControl("");

  lastResetUserEmail: string | null = null;
  temporaryPassword: string | null = null;

  constructor(
    private usersManagementService: UsersManagementService,
    private padlockService: PadlockService,
    private confirmationService: ConfirmationService,
    private toast: ToastService,
    private logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    const decoded = this.padlockService.getDecodedToken();
    this.isPlatformAdmin = decoded?.platform_admin === true;

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case "email":
          return (item.email || "").toLowerCase();
        case "username":
          return (item.username || "").toLowerCase();
        default:
          return (item as any)[property] || "";
      }
    };

    if (this.isPlatformAdmin) {
      this.loadUsers();
    }
  }

  loadUsers(): void {
    const search = (this.searchControl.value || "").trim();

    this.loading = true;
    this.loadError = null;

    this.usersManagementService.getUsers(search).subscribe({
      next: (users) => {
        this.users = users;
        this.dataSource.data = users;
        this.syncTableControls();

        if (this.tablePaginator) {
          this.tablePaginator.firstPage();
        }

        this.loading = false;
      },
      error: (error) => {
        this.logger.error("USERS MANAGEMENT: failed to load users", error);
        this.loadError = error;
        this.loading = false;
      },
    });
  }

  onSearch(): void {
    this.loadUsers();
  }

  clearSearch(): void {
    this.searchControl.setValue("");
    this.loadUsers();
  }

  confirmResetPassword(user: DirectoryUser): void {
    if (!this.isPlatformAdmin) {
      return;
    }

    const details: ConfirmationDetails = new ConfirmationDetails();
    details.title = `Reset password for \"${user.email}\"?`;
    details.message =
      "A temporary password will be generated. Share it securely with the user.";
    details.buttonLabel = "Reset Password";
    details.onConfirm = () => this.resetPassword(user);

    this.confirmationService.confirm(details);
  }

  confirmDeleteUser(user: DirectoryUser): void {
    if (!this.isPlatformAdmin) {
      return;
    }

    const details: ConfirmationDetails = new ConfirmationDetails();
    details.title = `Delete user \"${user.email}\"?`;
    details.message =
      "This action will permanently remove the user from the directory.";
    details.buttonLabel = "Delete User";
    details.destructiveButton = true;
    details.onConfirm = () => this.deleteUser(user);

    this.confirmationService.confirm(details);
  }

  dismissTemporaryPassword(): void {
    this.lastResetUserEmail = null;
    this.temporaryPassword = null;
  }

  trackByUser(_index: number, user: DirectoryUser): string {
    return user.uuid || user.email;
  }

  private syncTableControls(): void {
    this.dataSource.paginator = this.tablePaginator || null;
    this.dataSource.sort = this.tableSort || null;
  }

  private resetPassword(user: DirectoryUser): void {
    const userId = (user?.uuid || "").trim();
    if (!userId) {
      this.logger.error(
        "USERS MANAGEMENT: missing user UUID for reset password",
        user,
      );
      this.toast.openErrorToast("Unable to reset password for this user");
      return;
    }

    this.usersManagementService.resetPassword(userId).subscribe({
      next: (temporaryPassword) => {
        this.lastResetUserEmail = user.email;
        this.temporaryPassword = temporaryPassword;

        if (temporaryPassword) {
          this.toast.openToast("Temporary password generated successfully");
        } else {
          this.toast.openToast("Password reset completed");
        }
      },
      error: (error) => {
        this.logger.error("USERS MANAGEMENT: failed to reset password", error);
        this.toast.openErrorToast("Unable to reset password");
      },
    });
  }

  private deleteUser(user: DirectoryUser): void {
    const userId = (user?.uuid || "").trim();
    if (!userId) {
      this.logger.error(
        "USERS MANAGEMENT: missing user UUID for delete user",
        user,
      );
      this.toast.openErrorToast("Unable to delete this user");
      return;
    }

    this.usersManagementService.deleteUser(userId).subscribe({
      next: () => {
        this.users = this.users.filter(
          (candidate) => candidate.uuid !== userId,
        );
        this.dataSource.data = this.users;

        if (this.tablePaginator) {
          this.tablePaginator.firstPage();
        }

        this.toast.openToast("User deleted successfully");
      },
      error: (error) => {
        this.logger.error("USERS MANAGEMENT: failed to delete user", error);
        this.toast.openErrorToast("Unable to delete user");
      },
    });
  }
}
